import { getCurrencyFormatter, moneyFormat } from "@redotech/money/util";
import { PriceConstraintKind } from "@redotech/redo-conversion-core-common/redo-in-shipping/rate-table-constraints";
import {
  DisplayedShippingRateTable,
  dynamicRateRoundingToLabel,
  isDisplayedCustomRateTable,
  isDisplayedDynamicRateTable,
  isDisplayedFixedRateTable,
  isUnconstrainedStandaloneDynamicRateTableWithServiceLevel,
} from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  amazonServiceToLabel,
  apcServiceToLabel,
  australiaPostServiceToLabel,
  canadaPostServiceToLabel,
  dhlEcsServiceToLabel,
  dhlExpressServiceToLabel,
  epostGlobalServiceToLabel,
  fedexServiceToLabel,
  p2pServiceToLabel,
  purolatorServiceToLabel,
  speeDeeServiceToLabel,
  upsServiceToLabel,
  uspsServiceToLabel,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import CodeSnippetIcon from "@redotech/redo-icons/code-snippet-01.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import ThreeDotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import EditIcon from "@redotech/redo-icons/edit-01.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import Rows03Icon from "@redotech/redo-icons/rows-03.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import {
  RedoBadge,
  RedoBadgeSize,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { TextAndSupportingText } from "@redotech/redo-web/text-and-supporting-text";
import { BgColorValue, BorderColorValue } from "@redotech/redo-web/theme/box";
import { TextSizeValue } from "@redotech/redo-web/theme/typography";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { getTextFromConditions } from "@redotech/redo-web/utils/shipping-rates";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import * as css from "./shipping-method-item.module.css";
import { markupText } from "./utils";

interface ShippingMethodItemProps {
  rateTable: DisplayedShippingRateTable;
  isAutoSelected?: boolean;
  teamHasEnsureDiscountsCombineWithShippingDiscountsEnabled: boolean;
  onDelete?: (rateTableId: string) => Promise<void>;
  onEditClick?: () => void;
  onToggleAutoSelected?: (rateTableId: string) => Promise<void>;
  autoSelectDisabledReason?: string;
  onDuplicateClick?: () => Promise<void>;
  constrainAction?: {
    onConstrainRelativeToOtherRateTable: (
      targetRateTableId: string,
    ) => Promise<void>;
    otherRateTables: DisplayedShippingRateTable[];
  };
  newShippingMethodNameAlert?: { onLearnMore: () => void };
  styles?: BaseShippingMethodItemProps["styles"];
  useConstraintsV2: boolean;
}

export const ShippingMethodItem = memo(function ShippingMethodItem({
  rateTable,
  isAutoSelected = false,
  useConstraintsV2,
  teamHasEnsureDiscountsCombineWithShippingDiscountsEnabled,
  onDelete,
  onEditClick,
  onToggleAutoSelected,
  autoSelectDisabledReason,
  onDuplicateClick,
  constrainAction,
  newShippingMethodNameAlert,
  styles,
}: ShippingMethodItemProps) {
  const shippingMethodItemBaseProps = useShippingMethodItemBaseProps({
    rateTable,
    isAutoSelected,
    useConstraintsV2,
  });

  const nonZeroDiscountEnabled = !!rateTable.shippingDiscountRules?.some(
    (discountRule) =>
      (isDef(discountRule.discount.amount?.value) &&
        Number(discountRule.discount.amount.value) > 0) ||
      (isDef(discountRule.discount.percentage) &&
        Number(discountRule.discount.percentage) > 0),
  );

  const showSuggestShippingSystemWarningBadge =
    isDef(constrainAction) &&
    isUnconstrainedStandaloneDynamicRateTableWithServiceLevel({
      rateTable,
      otherRateTables: constrainAction.otherRateTables,
    });

  return (
    <>
      {shippingMethodItemBaseProps.map((baseProps, index) => (
        <BaseShippingMethodItem
          key={index}
          {...baseProps}
          autoSelectDisabledReason={autoSelectDisabledReason}
          constrainAction={
            isDisplayedCustomRateTable(rateTable) ? undefined : constrainAction
          }
          isAutoSelected={isAutoSelected}
          newShippingMethodNameAlert={newShippingMethodNameAlert}
          onDelete={onDelete ? () => onDelete?.(rateTable.id) : undefined}
          onDuplicateClick={onDuplicateClick}
          onEditClick={onEditClick}
          onToggleAutoSelected={
            onToggleAutoSelected
              ? () => onToggleAutoSelected?.(rateTable.id) ?? Promise.resolve()
              : undefined
          }
          showDiscountWarningBadge={
            !teamHasEnsureDiscountsCombineWithShippingDiscountsEnabled &&
            nonZeroDiscountEnabled
          }
          showSuggestShippingSystemWarningBadge={
            showSuggestShippingSystemWarningBadge
          }
          styles={styles}
        />
      ))}
    </>
  );
});

interface BaseShippingMethodItemProps {
  name: string;
  description: string;
  price: ReactNode;
  /**
   * Optional icon rendered before the title. Used by CUSTOM rate tables to
   * visually signal "this row is code-controlled" — for other types it's
   * left undefined.
   */
  leadingIcon?: ReactNode;
  /**
   * When set, the row tells the user that the rendered `name` came from the
   * merchant rather than from the customer-facing rate (CUSTOM rate tables
   * use this — their `name` is an admin-only label).
   */
  nameIsInternalOnly?: boolean;
  onDelete?: () => void;
  onEditClick?: () => void;
  onDuplicateClick?: () => Promise<void>;
  isAutoSelected: boolean;
  onToggleAutoSelected?: () => Promise<void>;
  autoSelectDisabledReason?: string;
  constrainAction?: {
    onConstrainRelativeToOtherRateTable: (
      targetRateTableId: string,
    ) => Promise<void>;
    otherRateTables: DisplayedShippingRateTable[];
  };
  badgeItems: string[];
  newShippingMethodNameAlert?: { onLearnMore: () => void };
  styles?: {
    container?: { bgColor?: BgColorValue; borderColor?: BorderColorValue };
  };
  showDiscountWarningBadge: boolean;
  showSuggestShippingSystemWarningBadge: boolean;
}

type CoreShippingMethodDisplayProps =
  | {
      readOnly?: false | undefined;
      name: string;
      description: string;
      price: ReactNode;
      leadingIcon?: ReactNode;
      nameIsInternalOnly?: boolean;
      isAutoSelected: boolean;
      onDelete?: () => void;
      onEditClick?: () => void;
      onDuplicateClick?: () => Promise<void>;
      onToggleAutoSelected?: () => Promise<void>;
      autoSelectDisabledReason?: string;
      constrainAction?: {
        onConstrainRelativeToOtherRateTable: (
          targetRateTableId: string,
        ) => Promise<void>;
        otherRateTables: DisplayedShippingRateTable[];
      };
    }
  | {
      readOnly: true;
      name: string;
      description: string;
      price: ReactNode;
      leadingIcon?: ReactNode;
      nameIsInternalOnly?: boolean;
      isAutoSelected: boolean;
    };

export const CoreShippingMethodDisplay = memo(
  function CoreShippingMethodDisplay({
    name,
    description,
    price,
    leadingIcon,
    nameIsInternalOnly,
    isAutoSelected,
    ...rest
  }: CoreShippingMethodDisplayProps) {
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    const [actionsDropdownButtonRef, setActionsDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);

    const listItems = useMemo<RedoListItem<string>[]>(() => {
      if (rest.readOnly) {
        return [];
      }

      const items: RedoListItem<string>[] = [];

      if (rest.onEditClick) {
        items.push({
          id: "edit",
          value: "edit",
          type: "text",
          text: "Edit",
          leadingItem: { type: "icon", Icon: EditIcon },
        });
      }

      if (rest.onToggleAutoSelected) {
        items.push({
          id: "toggle-auto-select",
          value: "toggle-auto-select",
          type: "text",
          text: isAutoSelected
            ? "Remove auto-selection"
            : "Auto-select this option at checkout",
          leadingItem: { type: "icon", Icon: CheckCircleIcon },
        });
      }

      if (rest.onDuplicateClick) {
        items.push({
          id: "duplicate",
          value: "duplicate",
          type: "text",
          text: "Duplicate",
          leadingItem: { type: "icon", Icon: CopyIcon },
        });
      }

      if (
        rest.constrainAction &&
        rest.constrainAction.otherRateTables.length > 0
      ) {
        items.push({
          id: "constrain-relative-to-other-rate-table",
          value: "constrain-relative-to-other-rate-table",
          type: "parent",
          text: "Constrain relative to",
          leadingItem: { type: "icon", Icon: Rows03Icon },
          children: rest.constrainAction.otherRateTables.map((rateTable) => ({
            id: rateTable.id,
            value: `constrain-to-${rateTable.id}`,
            type: "text" as const,
            text: rateTable.name,
          })),
        });
      }

      if (rest.onDelete) {
        items.push({
          id: "delete",
          value: "delete",
          type: "text",
          text: "Delete",
          leadingItem: { type: "icon", Icon: TrashIcon },
        });
      }

      return items;
    }, [rest, isAutoSelected]);

    const handleItemSelected = useMemo(() => {
      if (rest.readOnly) {
        return () => {};
      }

      return ({
        item,
      }: {
        item: RedoListItem<string>;
        source: "keyboard" | "mouse";
        index: number;
        selected: boolean;
      }) => {
        switch (item.value) {
          case "edit": {
            rest.onEditClick?.();

            break;
          }
          case "toggle-auto-select": {
            void rest.onToggleAutoSelected?.();

            break;
          }
          case "duplicate": {
            void rest.onDuplicateClick?.();

            break;
          }
          case "delete": {
            rest.onDelete?.();

            break;
          }
          default:
            if (item.value.startsWith("constrain-to-")) {
              void rest.constrainAction?.onConstrainRelativeToOtherRateTable?.(
                item.id.toString(),
              );
            }
        }
      };
    }, [rest]);

    return (
      <Flex
        align="center"
        className={css.titleRowCol}
        dir="row"
        gap={leadingIcon ? "md" : "none"}
        w="full"
      >
        {leadingIcon}
        <Flex dir="column" flex={1}>
          {nameIsInternalOnly && (
            <Text
              className={css.internalLabel}
              fontSize="xxs"
              fontWeight="medium"
              textColor="secondary"
            >
              Internal label (not shown to customers)
            </Text>
          )}
          <TextAndSupportingText
            mainTextSize="sm"
            supportingText={description}
            supportingTextSize="xs"
            text={name}
          />
        </Flex>
        <Flex align="center" dir="row" gap="xl" shrink={0}>
          {price}
          {!rest.readOnly && listItems.length > 0 && (
            <>
              <RedoButton
                aria-label="More options"
                dataTestId={`shipping-method-command-menu-${name}`}
                hierarchy="tertiary"
                IconLeading={ThreeDotsHorizontalIcon}
                onClick={() => setActionsDropdownOpen(true)}
                ref={setActionsDropdownButtonRef}
                size="sm"
              />
              <RedoListDropdown<string>
                disabledItems={
                  !rest.readOnly && rest.autoSelectDisabledReason
                    ? ["toggle-auto-select"]
                    : undefined
                }
                disabledTooltips={
                  !rest.readOnly && rest.autoSelectDisabledReason
                    ? { "toggle-auto-select": rest.autoSelectDisabledReason }
                    : undefined
                }
                dropdownAnchor={actionsDropdownButtonRef}
                dropdownOpen={actionsDropdownOpen}
                fitToAnchor={false}
                items={listItems}
                itemSelected={handleItemSelected}
                selectedItems={undefined}
                setDropdownOpen={setActionsDropdownOpen}
              />
            </>
          )}
        </Flex>
      </Flex>
    );
  },
);

const BaseShippingMethodItem = memo(function BaseShippingMethodItem({
  name,
  description,
  price,
  leadingIcon,
  nameIsInternalOnly,
  badgeItems,
  isAutoSelected,
  onDelete,
  onEditClick,
  onDuplicateClick,
  onToggleAutoSelected,
  autoSelectDisabledReason,
  constrainAction,
  newShippingMethodNameAlert,
  styles,
  showDiscountWarningBadge,
  showSuggestShippingSystemWarningBadge,
}: BaseShippingMethodItemProps) {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [badgeItemsToDisplay, setBadgeItemsToDisplay] =
    useState<string[]>(badgeItems);
  const [hiddenBadgeItems, setHiddenBadgeItems] = useState<string[]>([]);
  const [checkWidthTrigger, setCheckWidthTrigger] = useState<symbol>(Symbol());
  const [
    newShippingMethodNameAlertDismissed,
    setNewShippingMethodNameAlertDismissed,
  ] = useState(false);

  useEffect(() => {
    if (!containerRef) {
      return;
    }

    const actualWidth = containerRef.scrollWidth || 0;
    const maxWidth = containerRef.clientWidth || Infinity;

    if (actualWidth > maxWidth) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setHiddenBadgeItems((prev) => [
        ...prev,
        badgeItemsToDisplay[badgeItemsToDisplay.length - 1],
      ]);
      setBadgeItemsToDisplay((prevTags) => prevTags.slice(0, -1));
      setCheckWidthTrigger(Symbol());
    }
  }, [containerRef, checkWidthTrigger, badgeItemsToDisplay]);

  return (
    <Flex
      bgColor={styles?.container?.bgColor ?? "primary"}
      borderColor={styles?.container?.borderColor ?? "primary"}
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      className={css.container}
      dir="column"
      gap="lg"
      px="xl"
      py="lg"
      w="full"
    >
      <CoreShippingMethodDisplay
        autoSelectDisabledReason={autoSelectDisabledReason}
        constrainAction={constrainAction}
        description={description}
        isAutoSelected={isAutoSelected}
        leadingIcon={leadingIcon}
        name={name}
        nameIsInternalOnly={nameIsInternalOnly}
        onDelete={onDelete}
        onDuplicateClick={onDuplicateClick}
        onEditClick={onEditClick}
        onToggleAutoSelected={onToggleAutoSelected}
        price={price}
        readOnly={false}
      />
      {isAutoSelected && (
        <Flex dir="row" gap="md" mt="xs">
          <RedoBadge color="success" size="xs" text="Auto-selected" />
        </Flex>
      )}
      {badgeItems.length > 0 && (
        <Flex dir="row" gap="md" overflow="visible" ref={setContainerRef}>
          {badgeItemsToDisplay.map((item, index) => (
            <div key={`${index}-${item}`}>
              <RedoBadge color="gray" size="xs" text={item} />
            </div>
          ))}
          {hiddenBadgeItems.length > 0 && (
            <Tooltip title={hiddenBadgeItems.join(", ")}>
              <div>
                <RedoBadge
                  color="gray"
                  size="xs"
                  text={`+${hiddenBadgeItems.length}`}
                />
              </div>
            </Tooltip>
          )}
        </Flex>
      )}
      {showDiscountWarningBadge && (
        <RedoBadge
          color="warning"
          segmentLeading={{ type: "icon", Icon: AlertTriangleIcon }}
          size="xs"
          text="Ensure active discounts combine with shipping discounts"
        />
      )}
      {showSuggestShippingSystemWarningBadge && (
        <RedoBadge
          color="warning"
          segmentLeading={{ type: "icon", Icon: AlertTriangleIcon }}
          size="xs"
          text="Consider using shipping system"
        />
      )}
      {!!newShippingMethodNameAlert && !newShippingMethodNameAlertDismissed && (
        <RedoAlert
          actions={[
            {
              text: "Dismiss",
              action: () => setNewShippingMethodNameAlertDismissed(true),
              hierarchy: "tertiary",
            },
            {
              text: "Learn more",
              action: () => newShippingMethodNameAlert.onLearnMore(),
              hierarchy: "tertiary",
            },
          ]}
          closeButton={{
            showCloseButton: true,
            onClose: () => setNewShippingMethodNameAlertDismissed(true),
          }}
          color="warning"
          description="This is a new shipping method name that may not be accounted for in OMS. Please add the shipping method to all appropriate OMS views and automations."
          dir="row"
          fullWidth
          title="New shipping method name"
        />
      )}
    </Flex>
  );
});

type DisplayTableRows =
  | { pending: true }
  | { pending: false; value: { label: string; value: string }[] };
export type ShippingMethodItemDisplayTableRows = DisplayTableRows;

export const InspectorShippingMethodItem = memo(
  function InspectorShippingMethodItem({
    rateTable,
    displayTableRows = { pending: false, value: [] },
    useConstraintsV2,
  }: {
    rateTable: DisplayedShippingRateTable;
    displayTableRows?: DisplayTableRows;
    useConstraintsV2: boolean;
  }) {
    const shippingMethodItemBaseProps = useShippingMethodItemBaseProps({
      rateTable,
      priceProps: { text: { fontSize: "xs" }, badge: { size: "xs" } },
      useConstraintsV2,
    });

    return shippingMethodItemBaseProps.map((baseProps, index) => {
      return (
        <InspectorShippingMethodItemComponent
          key={index}
          {...baseProps}
          additionalDescriptors={baseProps.badgeItems}
          displayTableRows={displayTableRows}
        />
      );
    });
  },
);

const InspectorShippingMethodItemComponent = memo(
  function InspectorShippingMethodItemComponent({
    name,
    description,
    price,
    additionalDescriptors,
    displayTableRows,
  }: {
    name: string;
    description: string;
    price: ReactNode;
    additionalDescriptors: string[];
    displayTableRows: DisplayTableRows;
  }) {
    return (
      <Flex
        bgColor="primary"
        borderColor="primary"
        borderRadius="lg"
        borderStyle="solid"
        borderWidth="1px"
        className={css.container}
        dir="column"
        gap="lg"
        px="xl"
        py="lg"
        w="full"
      >
        <Flex className={css.titleRowCol} dir="column" w="full">
          <Flex align="center" justify="space-between" w="full">
            <TextAndSupportingText
              color="secondary"
              mainTextSize="xs"
              supportingText={description}
              supportingTextSize="xxs"
              text={name}
            />
            {additionalDescriptors.length > 0 && (
              <Tooltip title={additionalDescriptors.join(", ")}>
                <div>
                  <InfoCircleIcon className={css.infoIcon} />
                </div>
              </Tooltip>
            )}
          </Flex>
          <Flex overflowX="auto">{price}</Flex>
          {displayTableRows.pending ? (
            <SkeletonText length={14} />
          ) : (
            displayTableRows.value.length > 0 && (
              <Flex dir="column" gap="md" w="full">
                {displayTableRows.value.map((row, index) => (
                  <Flex justify="space-between" key={index} w="full">
                    <Text
                      fontSize="xxs"
                      fontWeight="medium"
                      textColor="secondary"
                    >
                      {row.label}
                    </Text>
                    <Text
                      fontSize="xxs"
                      fontWeight="regular"
                      textColor="secondary"
                    >
                      {row.value}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )
          )}
        </Flex>
      </Flex>
    );
  },
);

export function useShippingMethodItemBaseProps({
  rateTable,
  priceProps,
  isAutoSelected = false,
  useConstraintsV2,
}: {
  isAutoSelected?: boolean;
  rateTable: DisplayedShippingRateTable;
  priceProps?:
    | {
        text?: { fontSize?: TextSizeValue | undefined } | undefined;
        badge?: { size?: RedoBadgeSize | undefined } | undefined;
      }
    | undefined;
  useConstraintsV2: boolean;
}) {
  const basePropsForShippingMethodItems = useMemo(() => {
    if (isDisplayedFixedRateTable(rateTable)) {
      return rateTable.rates.map((rate) => {
        const badgeItems = [
          ...(rateTable.coverageTypes?.return ? ["Returns coverage"] : []),
          ...(rateTable.coverageTypes?.packageProtection
            ? ["Package protection"]
            : []),
          ...getTextFromConditions(rate.conditions),
        ];
        return {
          isAutoSelected,
          badgeItems,
          description: rateTable.description,
          name: rateTable.name,
          price:
            +rate.price === 0 ? (
              <RedoBadge
                color="success"
                size={priceProps?.badge?.size ?? "sm"}
                text="Free"
              />
            ) : (
              <Text
                fontSize={priceProps?.text?.fontSize ?? "sm"}
                fontWeight="semibold"
                textColor="primary"
              >
                {getCurrencyFormatter(rate.currency, "symbol").format(
                  +rate.price,
                )}
              </Text>
            ),
        };
      });
    } else if (isDisplayedDynamicRateTable(rateTable)) {
      const dynamicBadgeItems = [
        ...(rateTable.coverageTypes?.return ? ["Returns coverage"] : []),
        ...(rateTable.coverageTypes?.packageProtection
          ? ["Package protection"]
          : []),
        ...rateTable.rates.map((rate) => {
          const carrier = rate.carrierService.carrier;
          switch (carrier) {
            case Carriers.FEDEX:
              return fedexServiceToLabel[rate.carrierService.service];
            case Carriers.UPS:
              return upsServiceToLabel[rate.carrierService.service];
            case Carriers.USPS:
              return uspsServiceToLabel[rate.carrierService.service];
            case Carriers.AMAZON:
              return amazonServiceToLabel[rate.carrierService.service];
            case Carriers.DHL_EXPRESS:
              return dhlExpressServiceToLabel[rate.carrierService.service];
            case Carriers.DHL_ECS:
              return dhlEcsServiceToLabel[rate.carrierService.service];
            case Carriers.SPEE_DEE:
              return speeDeeServiceToLabel[rate.carrierService.service];
            case Carriers.PUROLATOR:
              return purolatorServiceToLabel[rate.carrierService.service];
            case Carriers.CANADA_POST:
              return canadaPostServiceToLabel[rate.carrierService.service];
            case Carriers.EPOST_GLOBAL:
              return epostGlobalServiceToLabel[rate.carrierService.service];
            case Carriers.APC:
              return apcServiceToLabel[rate.carrierService.service];
            case Carriers.AUSTRALIA_POST:
              return australiaPostServiceToLabel[rate.carrierService.service];
            case Carriers.P2P_GLOBAL:
              return p2pServiceToLabel[rate.carrierService.service];
            default:
              return assertNever(carrier);
          }
        }),
        ...getTextFromConditions(rateTable.conditions ?? []),
        ...(rateTable.rounding
          ? [`Rounding: ${dynamicRateRoundingToLabel[rateTable.rounding]}`]
          : []),
      ];
      // Check if both bounds are set to 0 (essentially free shipping)
      const isFreeShipping =
        (useConstraintsV2 &&
          rateTable.priceConstraints?.kind === PriceConstraintKind.DEFAULT &&
          isDef(rateTable.priceConstraints?.priceConstraints?.atMost?.amount) &&
          +rateTable.priceConstraints.priceConstraints.atMost.amount === 0) ||
        (isDef(rateTable.priceBounds?.maximum) &&
          +rateTable.priceBounds.maximum === 0);

      const priceBoundMinimum =
        useConstraintsV2 &&
        rateTable.priceConstraints?.kind === PriceConstraintKind.DEFAULT &&
        isDef(rateTable.priceConstraints?.priceConstraints?.atLeast?.amount)
          ? +rateTable.priceConstraints.priceConstraints.atLeast.amount
          : rateTable.priceBounds?.minimum;
      const priceBoundMaximum =
        useConstraintsV2 &&
        rateTable.priceConstraints?.kind === PriceConstraintKind.DEFAULT &&
        isDef(rateTable.priceConstraints?.priceConstraints?.atMost?.amount)
          ? +rateTable.priceConstraints.priceConstraints.atMost.amount
          : rateTable.priceBounds?.maximum;
      const priceBoundCurrency =
        useConstraintsV2 &&
        rateTable.priceConstraints?.kind === PriceConstraintKind.DEFAULT
          ? rateTable.priceConstraints?.currency
          : rateTable.priceBounds?.currency;

      const priceBoundsBadges = isFreeShipping
        ? [
            <RedoBadge
              color="success"
              key="free"
              size={priceProps?.badge?.size ?? "sm"}
              text="Free"
            />,
          ]
        : [
            isDef(priceBoundMinimum) &&
            +priceBoundMinimum > 0 &&
            priceBoundCurrency ? (
              <RedoBadge
                color="blue"
                size={priceProps?.badge?.size ?? "sm"}
                text={`Min: ${getCurrencyFormatter(priceBoundCurrency, "symbol").format(+priceBoundMinimum)}`}
              />
            ) : undefined,
            isDef(priceBoundMaximum) && priceBoundCurrency ? (
              <RedoBadge
                color="blue"
                size={priceProps?.badge?.size ?? "sm"}
                text={`Max: ${moneyFormat(
                  { amount: +priceBoundMaximum, currency: priceBoundCurrency },
                  "symbol",
                )}`}
              />
            ) : undefined,
          ].filter(isDef);

      const allPriceBadges =
        priceBoundsBadges.length > 0 ? (
          <Flex dir="row" gap="md" wrap="wrap">
            <RedoBadge
              color="blue"
              size={priceProps?.badge?.size ?? "sm"}
              text={markupText({ rateTable, useConstraintsV2 })}
            />
            {priceBoundsBadges}
          </Flex>
        ) : (
          <RedoBadge
            color="blue"
            size={priceProps?.badge?.size ?? "sm"}
            text={markupText({ rateTable, useConstraintsV2 })}
          />
        );

      const baseProps = {
        badgeItems: dynamicBadgeItems,
        description: rateTable.description,
        name: rateTable.name,
        price: allPriceBadges,
      };
      return [baseProps];
    } else if (isDisplayedCustomRateTable(rateTable)) {
      // CUSTOM rate tables have no customer-facing static name, description,
      // or price — those are produced per-checkout by the artifact. We
      // repurpose `name` as an internal label so multiple custom tables
      // remain distinguishable in this list, and signal that by rendering a
      // leading code icon + "Internal label" eyebrow above the name.
      const customBadgeItems = [
        ...(rateTable.coverageTypes?.return ? ["Returns coverage"] : []),
        ...(rateTable.coverageTypes?.packageProtection
          ? ["Package protection"]
          : []),
      ];
      return [
        {
          badgeItems: customBadgeItems,
          description: "",
          leadingIcon: (
            <RedoFeaturedIcon
              color="gray"
              Icon={CodeSnippetIcon}
              shape="square"
              size="md"
            />
          ),
          name: rateTable.name || "Untitled custom rate",
          nameIsInternalOnly: true,
          price: (
            <RedoBadge
              color="gray"
              size={priceProps?.badge?.size ?? "sm"}
              text="Custom"
            />
          ),
        },
      ];
    } else {
      assertNever(rateTable);
    }
  }, [rateTable, priceProps, isAutoSelected, useConstraintsV2]);

  return basePropsForShippingMethodItems;
}
